import type { CatalogStation } from '../../domain/catalog';
import { enrichInput, type StationInput } from '../../domain/library';
import type { Station } from '../../domain/stations';
import { type Lang, type TranslationKey, translate } from '../../i18n';
import { el } from '../../lib/dom';
import { createField, createModal } from './modal';

/**
 * Obrazec za postajo in potrditev brisanja — oba na skupni lupini
 * (`./modal.ts`, DS `.cm-scrim` / `.cm-box`).
 *
 * Dva modala in ne eden: potrditev brisanja mora biti **nepovratno dejanje
 * z lastnim korakom** (P2 §4.7). En sam modal, ki bi preklapljal vsebino,
 * bi ju lahko pomešal — in edini klik, ki podatke izbriše, je ravno tisti,
 * pri katerem si tega ne smemo privoščiti.
 */

export type ValidationError = 'name' | 'url' | 'duplicate';

export interface StationDialogDeps {
  /** Vrne napako ali `null`, kadar je shranjeno. Podvajanje ve le knjižnica. */
  onSubmit(input: StationInput, editingId: string | null): ValidationError | null;
  onDelete(stationId: string): void;
  /**
   * Poišče zadetek v katalogu, ki se ujema z naslovom — podlaga za samodejno
   * dopolnjevanje (P2 §1.12). `null`, kadar vir ne odgovori ali ujemanja ni;
   * dopolnjevanje je usluga in neuspeh ne sme ustaviti dodajanja postaje.
   */
  lookup(name: string, url: string): Promise<CatalogStation | null>;
}

export interface StationDialog {
  openAdd(): void;
  openEdit(station: Station): void;
  confirmDelete(station: Station): void;
  setLang(lang: Lang): void;
}

export function createStationDialog(root: HTMLElement, deps: StationDialogDeps): StationDialog {
  let lang: Lang = 'en';

  const form = createModal('nw-form-title');
  const name = createField('nw-station-name');
  const url = createField('nw-station-url', 'url');
  const tags = createField('nw-station-tags');
  const country = createField('nw-station-country');

  // Napaka ni klicaj in ne koda: pove, kaj se je zgodilo in kako naprej (P4 §5).
  // `.hint` je stiliran le znotraj `.field`, zato ima tudi napaka svoj ovoj.
  const error = el('div', { class: 'hint error', role: 'alert', hidden: '' });
  const tagsHint = el('div', { class: 'hint' });
  const countryHint = el('div', { class: 'hint' });
  tags.wrap.append(tagsHint);
  country.wrap.append(countryHint);

  // Samodejno dopolnjevanje (P2 §1.12) — izrecno dejanje, ne tiha magija: gumb
  // poišče postajo v katalogu in zapolni prazna polja. Status je `role=status`,
  // da bralnik izid naznani. Vidno le pri dodajanju (glej `openAdd`/`openEdit`).
  const enrich = el('button', { class: 'btn secondary', type: 'button' });
  const enrichStatus = el('div', { class: 'hint', role: 'status', 'aria-live': 'polite' });
  const enrichRow = el('div', { class: 'field' }, [enrich, enrichStatus]);

  form.body.append(
    name.wrap,
    url.wrap,
    tags.wrap,
    country.wrap,
    enrichRow,
    el('div', { class: 'field' }, [error]),
  );

  const confirm = createModal('nw-confirm-title');
  const confirmBody = el('p', { class: 'desc' });
  confirm.body.append(confirmBody);
  confirm.confirm.classList.remove('primary');
  confirm.confirm.classList.add('danger');

  root.append(form.scrim, confirm.scrim);

  /* ── Obrazec ────────────────────────────────────────────────────── */

  let editingId: string | null = null;
  /**
   * Zadetek iz kataloga, potrjen za tekoči naslov. Ob shranjevanju iz njega
   * `enrichInput()` prevzame še polja, ki jih obrazec ne kaže (bitrate, kodek,
   * sourceUuid …). Ob vsaki spremembi naslova pade — sicer bi pripadal drugi
   * postaji, kot piše v polju.
   */
  let pendingEnrichment: CatalogStation | null = null;
  let enrichStatusKey: TranslationKey | null = null;

  function showError(key: ValidationError | null): void {
    error.toggleAttribute('hidden', key === null);
    if (key === null) return;
    error.textContent = translate(lang, `station.form.error.${key}` as TranslationKey);
  }

  function showEnrichStatus(key: TranslationKey | null): void {
    enrichStatusKey = key;
    enrichStatus.textContent = key === null ? '' : translate(lang, key);
  }

  function readInput(): StationInput {
    const input: StationInput = {
      name: name.input.value,
      url: url.input.value,
      tags: tags.input.value.split(','),
    };
    const chosen = country.input.value.trim().toUpperCase();
    if (chosen !== '') input.countryCode = chosen;
    return input;
  }

  function submit(): void {
    let input = readInput();
    // Dopolnjevanje velja le za novo postajo (P2 §1.12): urejanje gre skozi
    // `StationPatch`, ki prevzetih polj (bitrate, kodek, sourceUuid) ne nosi.
    if (editingId === null && pendingEnrichment !== null) {
      input = enrichInput(input, pendingEnrichment);
    }

    const failure = deps.onSubmit(input, editingId);
    showError(failure);
    if (failure === null) form.hide();
  }

  async function runEnrich(): Promise<void> {
    const stationName = name.input.value.trim();
    const address = url.input.value.trim();
    // Vir išče po imenu in ujemamo po naslovu — brez obeh iskati ni česa.
    if (stationName === '' || !/^https?:\/\/.+/i.test(address)) {
      showEnrichStatus('station.form.enrich.need');
      return;
    }

    enrich.disabled = true;
    showEnrichStatus('station.form.enrich.busy');
    const match = await deps.lookup(stationName, address);
    enrich.disabled = false;

    if (match === null) {
      showEnrichStatus('station.form.enrich.none');
      return;
    }

    pendingEnrichment = match;
    // Zapolnimo le vidni polji, in le kadar sta prazni — vpisanega ne povozimo.
    // Ostala polja prevzame `enrichInput()` ob shranjevanju.
    if (tags.input.value.trim() === '' && match.tags.length > 0) {
      tags.input.value = match.tags.join(', ');
    }
    if (country.input.value.trim() === '' && match.countryCode !== undefined) {
      country.input.value = match.countryCode;
    }
    showEnrichStatus('station.form.enrich.done');
  }

  form.confirm.addEventListener('click', submit);
  enrich.addEventListener('click', () => {
    void runEnrich();
  });
  // Naslov je ključ ujemanja: ko se spremeni, potrjeni zadetek ne velja več.
  url.input.addEventListener('input', () => {
    pendingEnrichment = null;
    if (enrichStatusKey !== null) showEnrichStatus(null);
  });
  // Enter v polju shrani — obrazec brez tega zahteva pot do gumba z miško.
  for (const input of [name.input, url.input, tags.input, country.input]) {
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') submit();
    });
  }

  function fill(station: Station | null): void {
    editingId = station?.id ?? null;
    name.input.value = station?.name ?? '';
    url.input.value = station?.url ?? '';
    tags.input.value = station?.tags.join(', ') ?? '';
    country.input.value = station?.countryCode ?? '';
    showError(null);
    pendingEnrichment = null;
    showEnrichStatus(null);
  }

  /* ── Prevodi ────────────────────────────────────────────────────── */

  function applyLang(): void {
    name.label.textContent = translate(lang, 'station.form.name');
    url.label.textContent = translate(lang, 'station.form.url');
    tags.label.textContent = translate(lang, 'station.form.tags');
    country.label.textContent = translate(lang, 'station.form.country');
    tagsHint.textContent = translate(lang, 'station.form.tags.hint');
    countryHint.textContent = translate(lang, 'station.form.country.hint');
    enrich.textContent = translate(lang, 'station.form.enrich');
    // Status je lahko že izpisan; ob menjavi jezika ga prevedemo znova.
    if (enrichStatusKey !== null) enrichStatus.textContent = translate(lang, enrichStatusKey);
    url.input.placeholder = 'https://';

    form.close.setAttribute('aria-label', translate(lang, 'dialog.close'));
    form.cancel.textContent = translate(lang, 'dialog.cancel');
    form.confirm.textContent = translate(lang, 'dialog.save');

    confirm.close.setAttribute('aria-label', translate(lang, 'dialog.close'));
    confirm.cancel.textContent = translate(lang, 'dialog.cancel');
    confirm.confirm.textContent = translate(lang, 'station.delete');
    confirm.title.textContent = translate(lang, 'station.delete.title');
  }

  return {
    setLang(next) {
      lang = next;
      applyLang();
      form.title.textContent = translate(
        lang,
        editingId === null ? 'stations.add' : 'station.edit',
      );
    },

    openAdd() {
      fill(null);
      // Dopolnjevanje je »ob dodajanju« (P2 §1.12); pri urejanju ga skrijemo.
      enrichRow.hidden = false;
      form.title.textContent = translate(lang, 'stations.add');
      form.show(name.input);
    },

    openEdit(station) {
      fill(station);
      enrichRow.hidden = true;
      form.title.textContent = translate(lang, 'station.edit');
      form.show(name.input);
    },

    confirmDelete(station) {
      confirmBody.textContent = translate(lang, 'station.delete.body', { station: station.name });
      // Fokus gre na »prekliči«, ne na »izbriši«: privzeti cilj tipke Enter
      // pri nepovratnem dejanju ne sme biti to dejanje.
      confirm.show(confirm.cancel);

      confirm.confirm.onclick = () => {
        deps.onDelete(station.id);
        confirm.hide();
      };
    },
  };
}
